import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import { newId } from "../model/ids";
import { withDecoration } from "../model/projectOps";
import { pathFromBuffer, setPathPoints } from "../canvas/Scene";
import { cssVar } from "../colors";
import {
  nextStampDecorationName,
  type LineStampDecoration,
} from "../model/Project";

/**
 * Drag to draw a path; on release, creates a lineStamp decoration along the path.
 * Holding shift on release closes the path.
 */
export class LineStampTool extends ToolBase {
  id = "lineStamp";
  label = "Line stamp";
  shortcut = "d";
  cursor = "crosshair";

  private buffer: { x: number; y: number }[] = [];
  private preview: paper.Path | null = null;

  override onDown(_s: PointerSample, p: { x: number; y: number }) {
    this.buffer = [{ x: p.x, y: p.y }];
    this.startPreview();
  }

  override onMove(_s: PointerSample, p: { x: number; y: number }) {
    const last = this.buffer[this.buffer.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1.0) {
      this.buffer.push({ x: p.x, y: p.y });
      this.updatePreview();
      this.updateLengthGuide();
    }
  }

  /** Live total-path-length readout while dragging (#920) — a ray from the
   *  path's start to its current end, labelled with the accumulated length so
   *  the maker sees how long a run they're stamping. Toggle-gated (Stamping
   *  rays default off); needs at least a two-point path. */
  private updateLengthGuide() {
    if (this.buffer.length < 2) return;
    let len = 0;
    for (let i = 1; i < this.buffer.length; i++) {
      const a = this.buffer[i - 1]!;
      const b = this.buffer[i]!;
      len += Math.hypot(b.x - a.x, b.y - a.y);
    }
    const start = this.buffer[0]!;
    const end = this.buffer[this.buffer.length - 1]!;
    this.ctx.scene.setGuideRays([
      {
        from: { x: start.x, y: start.y },
        to: { x: end.x, y: end.y },
        text: this.ctx.scene.formatDragDistance(len),
      },
    ]);
  }

  override onUp(s: PointerSample, _p: { x: number; y: number }) {
    if (this.buffer.length < 2) {
      this.cleanup();
      return;
    }
    const stampId = this.ctx.settings.activeStampId;
    if (!stampId) {
      this.cleanup();
      return;
    }
    const meta = this.ctx.library.get(stampId);
    if (!meta) {
      this.cleanup();
      return;
    }

    const closed = s.shiftKey;
    const data = pathFromBuffer(this.buffer) + (closed ? " Z" : "");
    const scope = this.ctx.scene.getPaperScope();
    const tmp = new scope.CompoundPath(data);
    const center = tmp.bounds.center;
    tmp.remove();
    const hit = this.ctx.scene.hitTest(center);
    const pieceId =
      hit.pieceId ??
      this.ctx.scene.getSelection().pieceId ??
      this.ctx.history.state.pieces[0]?.id;
    this.cleanup();
    if (!pieceId) return;

    const targetPiece = this.ctx.history.state.pieces.find(
      (p) => p.id === pieceId,
    );
    const baseName = `${meta.name} (line)`;
    const name = targetPiece
      ? nextStampDecorationName(targetPiece, baseName)
      : baseName;
    const dec: LineStampDecoration = {
      id: newId("dec"),
      kind: "lineStamp",
      name,
      visible: true,
      depth: meta.deboss ? -0.6 : 0.6,
      stampId,
      pathData: data,
      spacing: this.ctx.settings.spacing,
      scale: 1,
      rotateAlong: this.ctx.settings.rotateAlong,
      closed,
    };
    this.ctx.commit("Line stamp", (proj) => withDecoration(proj, pieceId, dec));
  }

  override onCancel() {
    this.cleanup();
  }

  private startPreview() {
    const scope = this.ctx.scene.getPaperScope();
    this.preview = new scope.Path();
    this.preview.strokeColor = new scope.Color(cssVar("--canvas-accent"));
    this.preview.strokeWidth = 1;
    this.preview.dashArray = [3, 3];
    this.preview.fillColor = null;
    this.preview.data = { ephemeral: true };
  }

  private updatePreview() {
    if (!this.preview) return;
    const scope = this.ctx.scene.getPaperScope();
    this.preview.removeSegments();
    setPathPoints(
      this.preview,
      this.buffer.map((p) => new scope.Point(p.x, p.y)),
    );
  }

  private cleanup() {
    if (this.preview) {
      this.preview.remove();
      this.preview = null;
    }
    this.buffer = [];
    this.ctx.scene.setGuideRays(null);
  }
}
